import React, { useState } from 'react';

import {Button, useMutation, useNotify, useRedirect} from 'react-admin'
import {connect} from "react-redux";
import _ from 'lodash';
import PayrollValidationDialog from './PayrollValidationDialog';
import { fetchODataSchema } from '../../Helpers/odataServiceProvider';

function cleanPayroll (payRun) {
    console.log({payRun})
    payRun.runDate = new Date()
    // Convert all relevant date fields to ISO strings if they are Date or Moment objects
    function toISO(val) {
        if (val && typeof val.isValid === 'function' && val.isValid() && typeof val.toISOString === 'function') return val.toISOString(); // Moment.js
        if (val instanceof Date) return val.toISOString();
        return val;
    }
    payRun.runDate = toISO(payRun.runDate);
    payRun.payrollDate = toISO(payRun.payrollDate);
    payRun.periodStart = toISO(payRun.periodStart);
    payRun.periodEnd = toISO(payRun.periodEnd);


    // console.log({payRun})


    // Add initial audit log meta
    if (!payRun.auditLogMeta) {
        // console.log(payRun)
        payRun.auditLogMeta = [{
            type: 'created',
            date: new Date().toISOString(), // Ensure date is in ISO format
            user: localStorage.getItem('user'),
            net: payRun.cash,
            payrollId: payRun.id,
            description: 'Initial payroll run creation',
            userRoles: JSON.parse(localStorage.getItem('roles') || '[]')
        }]
    } else {
        
           // If this is an update (has an ID), append to audit log
           if (payRun.id) {
            // const existingPayroll = yield call(OdataServiceProvider.getOne, 'payrollRuns', {id: data.id})
            // const auditLogMeta = existingPayroll.data.auditLogMeta || []
            payRun.auditLogMeta = [...payRun.auditLogMeta, {
                type: 'edited',
                date: new Date().toISOString(), // Ensure date is in ISO format
                net: payRun.cash,
                payrollId: payRun.id,
                user: localStorage.getItem('user'),
                description: payRun.description || 'Payroll run updated',
                userRoles: JSON.parse(localStorage.getItem('roles') || '[]')
            }]
        }

        payRun.auditLogMeta = payRun.auditLogMeta.map(log => ({
            ...log,
            date:  (typeof log.date === 'string' || log.date instanceof Date) ? new Date(log.date).toISOString() : new Date().toISOString()  // Ensure date is a Date object
        }));

    }

    const payrolls = payRun.payrolls.map(p => {
        const np = { ...p };
        np.payrollDate = toISO(np.payrollDate);
        np.periodStart = toISO(np.periodStart);
        np.periodEnd = toISO(np.periodEnd);
        return _.omit(np,["loans","leaveDaysTakenPrevious",'grossIncome','fixedDeductionsTotalsAndIncomeTax','additionalFixedEarningsOriginal','payrollDate','periodDaySetting', 'costCenterCosts', 'useServiceHours', 'deductServiceHour', 'daysInMonthForOT', 'daysInMonthForExpectedHours', 'employee','startDate', 'contractEndDate', 'distEarningsOnActualDate','ignoreLeaveOnServiceHours', 'periodStart', 'periodEnd', 'id', 'remarkNotes', 'reviewNotes', 'settings', 'serviceDaysModified'])
    });
    return {..._.omit(payRun,["id",'prevPayrollDate','firstRun','excluded', 'actingAllowancePercentage', 'checkpoints', 'unProccesedTimeSheet']),payrolls}
}

// Type check helper
function isType(value, edmType) {
    if (value === null || value === undefined) return true; // nullability checked elsewhere
    // Handle collection types - they should be validated by collection logic, not here
    if (edmType && typeof edmType === 'string' && edmType.startsWith('Collection(')) {
        return true; // Skip type check for collections, let collection validation handle it
    }
    if (edmType && edmType.indexOf('Models.') > 0) {
        // Enum type: allow string or number
        return typeof value === 'string' || typeof value === 'number';
    }
    // console.log(edmType,typeof value)
    switch (edmType) {
        case 'Edm.String': return typeof value === 'string';
        case 'Edm.Int32':
        case 'Edm.Int16':
        case 'Edm.Int64':
        case 'Edm.Decimal':
        case 'Edm.Double':
        case 'Edm.Single': return (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && !isNaN(Number(value));
        case 'Edm.Boolean': return typeof value === 'boolean' || (typeof value === 'string' && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false'));
        case 'Edm.DateTimeOffset':
        case 'Edm.Date':
            // Accept valid Date objects or values that can be parsed as dates
            if (Object.prototype.toString.call(value) === '[object Date]' && !isNaN(value.getTime())) return true;
            if (typeof value === 'string' || typeof value === 'number') return !isNaN(Date.parse(value));
            return false;
        default: return true; // fallback: skip type check
    }
}

const IGNORED_FIELDS = ['id', 'createdDate'];

async function validatePayrollDataWithSchema(payrollRun) {
    const errors = [];
    const errorKeys = new Set(); // Track unique error keys to prevent duplicates
    const { schemas, enumTypes } = await fetchODataSchema();
    // Find correct entity names (case-insensitive match for 'payrollRuns' and 'payrolls')
    const payrollRunSchema = schemas['payrollRuns'] || schemas['PayrollRun'] || schemas['PayrollRuns'];
    const payrollSchema = schemas['payrolls'] || schemas['Payroll'] || schemas['Payrolls'];

    // Helper to validate one record (payrollRun or payroll)
    function validateRecord(record, schema, recordType, name) {
        if (!schema) return;
        const schemaFields = schema.fields || {};
        const navigationFields = schema.navigation || {};

        // Helper to add error with deduplication
        function addError(errorObj) {
            const errorKey = `${errorObj.recordType}|${errorObj.field}|${errorObj.error}`;
            if (!errorKeys.has(errorKey)) {
                errorKeys.add(errorKey);
                errors.push(errorObj);
            }
        }

        // Check for required fields and type (properties only)
        Object.entries(schemaFields).forEach(([field, def]) => {
            if (IGNORED_FIELDS.includes(field)) return;
            const value = record[field];

            // Numeric fields can be null (they are converted to 0)
            const numericTypes = ['Edm.Int32', 'Edm.Int16', 'Edm.Int64', 'Edm.Decimal', 'Edm.Double', 'Edm.Single'];
            if (numericTypes.includes(def.type)) {
                if (value !== undefined && value !== null && (value === '' || isNaN(Number(value)))) {
                    addError({
                        recordType,
                        field,
                        error: `should be of type Number`,
                        name: name || '',
                        value
                    });
                }
                // Do not check for required, as null is acceptable.
                return;
            }

            // Boolean fields can be null
            if (def.type === 'Edm.Boolean') {
                // If present, must be boolean or null
                if (!(value ==='true'|| value==='false') && value !== undefined && value !== null && typeof value !== 'boolean') {
                   
                    addError({
                        recordType,
                        field,
                        error: `should be of type Boolean`,
                        name: name || '',
                        value
                    });
                }
                // Do not require boolean fields even if required
                return;
            }
            // Enum fields (isEnum true)
            if (def.isEnum) {
                // Allow null/undefined, do not require even if required
                if (value !== undefined && value !== null && !isType(value, def.type)) {
                    addError({
                        recordType,
                        field,
                        error: `should be a string or number (enum)`,
                        name: name || '',
                        value
                    });
                }
                return;
            }
            // Complex types: validate recursively
            if (def.isComplex) {
                if (value !== undefined && value !== null) {
                    const complexTypeName = def.type.split('.').pop();
                    const complexSchema = schemas[complexTypeName];

                    if (complexSchema) {
                        // Validate complex type properties - only check fields that are actually present in the data
                        Object.keys(value).forEach(complexField => {
                            if (IGNORED_FIELDS.includes(complexField)) return;
                            const complexValue = value[complexField];
                            const complexDef = complexSchema.fields[complexField];

                                if (complexDef) {
                                 // Field exists in schema - validate it
                                 if (complexDef.required && (complexValue === null || complexValue === '')) {
                                     addError({
                                         recordType,
                                         field: `${field}.${complexField}`,
                                         error: 'is required',
                                         name: name || '',
                                         value: complexValue
                                     });
                                 } else if (!(complexValue==='true'||complexValue==='false')&&complexValue !== null && complexValue !== '' && !isType(complexValue, complexDef.type) && !(Array.isArray(complexValue) && complexValue.length === 0)) {

                                     console.log({complexValue,type:complexDef.type, valid:isType(complexValue,complexDef.type), t: typeof complexValue})

                                     addError({
                                         recordType,
                                         field: `${field}.${complexField}`,
                                         error: `should be of type ${complexDef.type.replace('Edm.', '')}`,
                                         name: name || '',
                                         value: complexValue
                                     });
                                 }
                             } else {
                                // Field does not exist in schema
                                addError({
                                    recordType,
                                    field: `${field}.${complexField}`,
                                    error: 'is not in schema',
                                    name: name || '',
                                    value: complexValue
                                });
                            }
                        });
                    }
                }
                return;
            }

// Collection types: validate each item in the collection
        if (def.isCollection || (typeof def.type === 'string' && def.type.startsWith('Collection('))) {
            if (value !== undefined && value !== null && Array.isArray(value)) {
                const collectionTypeName = def.type.substring(11, def.type.length - 1).split('.').pop();
                const itemSchema = schemas[collectionTypeName];

                    if (itemSchema) {
                        value.forEach((item, index) => {
                            if (item !== null && item !== undefined) {
                                // Validate each item in the collection - only check fields that are actually present
                                Object.entries(itemSchema.fields || {}).forEach(([itemField, itemDef]) => {
                                    if (IGNORED_FIELDS.includes(itemField)) return;
                                    const itemValue = item[itemField];

                                    // Only validate if the field is present in the data
                                    if (itemValue !== undefined) {
                                        // Handle required check for collection item properties (only if field is present)
                                        if (itemDef.required && (itemValue === null || itemValue === '')) {
                                            addError({
                                                recordType,
                                                field: `${field}[${index}].${itemField}`,
                                                error: 'is required',
                                                name: name || '',
                                                value: itemValue
                                            });
                                        } else if (itemValue !== null && itemValue !== '' && !isType(itemValue, itemDef.type)) {
                                            console.log(value, itemValue, "collectionValue")
                                            addError({
                                                recordType,
                                                field: `${field}[${index}].${itemField}`,
                                                error: `should be of type ${itemDef.type.replace('Edm.', '')}`,
                                                name: name || '',
                                                value: itemValue
                                            });
                                        }
                                    }
                                });

                                // Check for extra fields in collection items
                                Object.keys(item).forEach(itemField => {
                                    if (IGNORED_FIELDS.includes(itemField)) return;
                                    if (!itemSchema.fields[itemField]) {
                                        addError({
                                            recordType,
                                            field: `${field}[${index}].${itemField}`,
                                            error: 'is not in schema',
                                            name: name || '',
                                            value: item[itemField]
                                        });
                                    }
                                });
                            }
                        });
                    }
                }
                return;
            }
            // Required check (except for booleans/enums/complex/collection)
            if (def.required && (value === undefined || value === null || value === '')) {
                addError({
                    recordType,
                    field,
                    error: 'is required',
                    name: name || '',
                    value
                });
            } else if (value !== undefined && value !== null && value !== '' && !isType(value, def.type)) {
                  console.log(value, def.type, "collectionValue")
                addError({
                    recordType,
                    field,
                    error: `should be of type ${def.type.replace('Edm.', '')}`,
                    name: name || '',
                    value
                });
            }
        });

        // Check navigation properties recursively
        Object.entries(navigationFields).forEach(([field, def]) => {
            const value = record[field];
            if (value === undefined || value === null) return;

            const isCollection = def.type.startsWith('Collection(');
            const typeName = isCollection
                ? def.type.substring(11, def.type.length - 1)
                : def.type;
            const simpleTypeName = typeName.split('.').pop();
            const itemSchema = schemas[simpleTypeName] || schemas[typeName]  || schemas[simpleTypeName.toLowerCase()+"s"] || schemas[simpleTypeName+"s"];

            if (itemSchema) {
                if (isCollection) {
                    if (Array.isArray(value)) {
                        value.forEach((item, index) => {
                            // If we are at 'payrolls' under 'payrollRun', start the name with the employee's name.
                            const baseName = (recordType === 'payrollRun' && field === 'payrolls' && item.fullName)
                                ? item.fullName
                                : name;

                            const newName = baseName ? `${baseName} -> ${field}[${index}]` : `${field}[${index}]`;

                            const newRecordType = (recordType === 'payrollRun' && field === 'payrolls') ? 'payroll' : simpleTypeName;

                            validateRecord(item, itemSchema, newRecordType, newName);
                        });
                    }
                } else {
                    const newName = name ? `${name} -> ${field}` : field;
                    validateRecord(value, itemSchema, simpleTypeName, newName);
                }
            }
        });

        // Check for extra fields (not in properties or navigation)
        Object.keys(record).forEach(field => {
            if (IGNORED_FIELDS.includes(field)) return;
            if (!schemaFields[field] && !navigationFields[field]) {
                addError({
                    recordType,
                    field,
                    error: 'is not in schema',
                    name: name || (recordType==='payroll'?record.fullName:recordType),
                    value: record[field]
                });
            }
        });
    }

    // Validate payrollRun fields
    if (payrollRunSchema) {
        validateRecord(
            payrollRun,
            payrollRunSchema,
            'payrollRun',
            'Payroll Run'
        );
    }
    // Validate each payroll
    if (Array.isArray(payrollRun.payrolls) && payrollSchema) {
        payrollRun.payrolls.forEach((p, idx) => {
            validateRecord(
                p,
                payrollSchema,
                'payroll',
                p.fullName || `Payroll #${idx+1}`
            );
        });
    }
    return errors;
}

function validatePayrollLogic(payrollRun) {
    const results = [];
    const payrollsWithPension = payrollRun.payrolls.filter(p => p.empPension > 0);
    const pensionableSalarySum = _.sumBy(payrollsWithPension, 'basicSalary');

    // --- Employee Pension Rule ---
    let empPensionRate = 0.07; // default
    for (const p of payrollsWithPension) {
        if (!p.payrollDeductions) continue;
        const pensionDeduction = p.payrollDeductions.find(d => d.type === 'EmpPension');
        if (pensionDeduction && typeof pensionDeduction.rate === 'number') {
            empPensionRate = pensionDeduction.rate;
            break; 
        }
    }
    const calculatedEmpPension = pensionableSalarySum * empPensionRate;
    //The payrollRun.pensionEmployee is negative
    const empPensionPassed = Math.abs(calculatedEmpPension - payrollRun.pensionEmployee) < 0.1; 
    let empPensionDetails = [];
    if (!empPensionPassed) {
        empPensionDetails = payrollsWithPension.map(p => {
            let rate = empPensionRate;
            if (p.payrollDeductions) {
                const pensionDeduction = p.payrollDeductions.find(d => d.type === 'EmpPension');
                if (pensionDeduction && typeof pensionDeduction.rate === 'number') {
                    rate = pensionDeduction.rate;
                }
            }
            return {
                name: p.fullName || p.employeeName || p.employeeId || '',
                expected: (p.basicSalary || 0) * rate,
                actual: p.empPension || 0,
                basicSalary: p.basicSalary || 0,
                rate: rate
            };
        })
        .filter(d => Math.abs(d.expected - d.actual) >= 0.1);
    }
    results.push({
        name: 'Employee Pension Total',
        passed: empPensionPassed,
        expected: payrollRun.pensionEmployee,
        actual: calculatedEmpPension,
        details: empPensionDetails,
        type: 'error'
    });

    // --- Company Pension Rule ---
    let compPensionRate = 0.11; // default
    for (const p of payrollsWithPension) {
        if (!p.payrollExpenses) continue;
        const pensionExpense = p.payrollExpenses.find(d => d.type === 'CompanyPension');
        if (pensionExpense && typeof pensionExpense.rate === 'number') {
            compPensionRate = pensionExpense.rate;
            break;
        }
    }
    const calculatedCompPension = pensionableSalarySum * compPensionRate;
    const compPensionPassed = Math.abs(calculatedCompPension - payrollRun.pensionCompany) < 0.1;
    let compPensionDetails = [];
    if (!compPensionPassed) {
        compPensionDetails = payrollsWithPension.map(p => {
            let rate = compPensionRate;
            if (p.payrollExpenses) {
                const pensionExpense = p.payrollExpenses.find(d => d.type === 'CompanyPension');
                if (pensionExpense && typeof pensionExpense.rate === 'number') {
                    rate = pensionExpense.rate;
                }
            }
            return {
                name: p.fullName || p.employeeName || p.employeeId || '',
                expected: (p.basicSalary || 0) * rate,
                actual: p.companyPension || 0,
                basicSalary: p.basicSalary || 0,
                rate: rate
            };
        })
        .filter(d => Math.abs(d.expected - d.actual) >= 0.1);
    }
    results.push({
        name: 'Company Pension Total',
        passed: compPensionPassed,
        expected: payrollRun.pensionCompany,
        actual: calculatedCompPension,
        details: compPensionDetails,
        type: 'error'
    });

    // --- Unprocessed TimeSheet Rule ---
    if (payrollRun.unProccesedTimeSheet && payrollRun.unProccesedTimeSheet.length > 0) {
        results.push({
            name: 'Unprocessed Time Attendance',
            passed: false,
            expected: 0,
            actual: payrollRun.unProccesedTimeSheet.length,
            details: payrollRun.unProccesedTimeSheet.map(r => ({
                name: r.name,
                mateId: r.mateId,
                ot: r.ot,
                fullExpectedHrs: r.fullExpectedHrs,
                payHrs: r.payHrs
            })),
            type: 'error'
        });
    }

    return results;
}

function validatePayrollReasonableness(payrollRun) {
    const results = [];

    // Group remarks by class
    const remarkClasses = {};

    payrollRun.payrolls.forEach(payroll => {
        if (payroll.remarks && Array.isArray(payroll.remarks)) {
            payroll.remarks.forEach(remark => {
                if (!remarkClasses[remark.class]) {
                    remarkClasses[remark.class] = [];
                }
                remarkClasses[remark.class].push({
                    employeeName: payroll.fullName,
                    employeeId: payroll.employeeId,
                    remark: remark.text,
                    type: remark.type
                });
            });
        }
    });

    // Convert to results format
    Object.entries(remarkClasses).forEach(([className, employees]) => {
        const checkType = employees.some(e => e.type === 'check') ? 'check' : 'info';
        const displayName = className.charAt(0).toUpperCase() + className.slice(1).replace(/([A-Z])/g, ' $1');
        results.push({
            name: checkType === 'info' ? `${employees.length} ${displayName}` : `${displayName} Issues`,
            passed: checkType === 'info', // Info types are considered passed, check types are warnings
            expected: 0,
            actual: employees.length,
            details: employees.map(e => ({
                name: e.employeeName,
                remark: e.remark,
                expected: '-',
                actual: '-'
            })),
            class: className,
            type: checkType === 'check' ? 'warning' : 'info'
        });
    });

    return results;
}

const SavePayrollButton = props => {
    const notify = useNotify();
    const redirect = useRedirect();
    const [mutate, { loading }] = useMutation();
    const [validationErrors, setValidationErrors] = useState([]);
    const [logicChecks, setLogicChecks] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const resetValidation = () => {
        setValidationErrors([]);
        setLogicChecks([]);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        resetValidation();
    };

    const proceedToSave = async () => {
        setDialogOpen(false);
        resetValidation();
        const cleanedPayroll = cleanPayroll(props.runPayrollData);
        mutate({
            type: 'create',
            resource: 'payrollRuns',
            payload: {
                data: cleanedPayroll
            },
        }, {
            action: 'PAYROLL_RUN_SAVED',
            onSuccess: ({
                data
            }) => {
                redirect(`/runPayroll/${data.id}/reports`);
                notify('Payroll Saved Successfully');
            },
            onFailure: (error) => {
                notify(`Payroll can not saved right now, Please check the data or press re-set and try again`, 'warning')
            },
        });
    };

    const save = async () => {
        const logicResults = validatePayrollLogic(props.runPayrollData);
        const reasonablenessResults = validatePayrollReasonableness(props.runPayrollData);
        const cleanedPayroll = cleanPayroll(props.runPayrollData)
        // Validate before saving
        const schemaErrors = await validatePayrollDataWithSchema(cleanedPayroll);


        setValidationErrors(schemaErrors);
        setLogicChecks([...logicResults, ...reasonablenessResults]);
        setDialogOpen(true);
    }

    const numSchemaErrors = validationErrors.length;
    const numErrors = logicChecks.filter(c => c.type === 'error' && !c.passed).length;
    const numWarnings = logicChecks.filter(c => c.type === 'warning' && !c.passed).length;
    const hasErrors = numSchemaErrors > 0 || numErrors > 0;
    const hasWarnings = numWarnings > 0;
    const hasAnyErrors = hasErrors;

    const confirmContent = hasErrors ? `You are about to continue with ${numSchemaErrors > 0 ? `${numSchemaErrors} schema validation error(s)` : ''}${numSchemaErrors > 0 && numErrors > 0 ? ' and ' : ''}${numErrors > 0 ? `${numErrors} logic error(s)` : ''}${(numSchemaErrors > 0 || numErrors > 0) && numWarnings > 0 ? ' and ' : ''}${numWarnings > 0 ? `${numWarnings} warning(s)` : ''}. Are you sure you want to proceed?` : numWarnings > 0 ? `There are ${numWarnings} warning(s). You can proceed.` : '';

    return <>
        <Button  variant="contained" size="large" color={props.disabled?"secondary":"primary"} label="Save & Continue" onClick={save} disabled={loading || hasErrors}  />
        <PayrollValidationDialog
            open={dialogOpen}
            errors={validationErrors}
            logicChecks={logicChecks}
            onClose={handleDialogClose}
            onConfirm={proceedToSave}
            needsConfirmation={hasErrors}
            confirmTitle={hasErrors ? "Are you sure?" : "Warnings Detected"}
            confirmContent={confirmContent}
        />
    </>;

};

export default connect(state=>({runPayrollData:state.runPayroll}),dispatch=>{
    return {
        onSavePayroll:(runPayrollData) => {
         //   const {  incomeTaxRules, ...payrollRun } = runPayrollData;
            return dispatch({type:'SAVE_PAYROLL_RUN',payload:runPayrollData})
        },
    }
})(SavePayrollButton);
//export default SavePayrollButton

